/**
 * SDA-120 / SDA-137: Email Templates tab on /admin/campaigns?tab=templates
 *
 * Lists campaign templates from email_templates and lets the admin edit
 * subject + preview_text + html_content + plain_text_content + description
 * without a code deploy. Reuses RichTextEditor (TipTap).
 *
 * SDA-137 additions:
 *   - "+ New Template" button creates user-defined custom campaign templates.
 *   - System templates (the 5 originals the cron uses) and custom templates
 *     are shown in separate labelled sections.
 *   - Custom templates can be soft-deleted via a trash icon + ConfirmModal.
 *
 * Edits apply to NEW campaigns the cron creates. Pending chains keep their
 * already-rendered html_content. Banner explains.
 */

import { useEffect, useState } from 'react';
import RichTextEditor from '../../../components/RichTextEditor';
import ConfirmModal from '../../../components/ui/ConfirmModal';
import {
  getEmailTemplates,
  updateEmailTemplate,
  createEmailTemplate,
  deleteEmailTemplate,
  renderTemplatePreview,
  sendTestTemplateEmail,
  getShows,
} from '../../../api/admin';
import type { EmailTemplate, Show } from '../../../api/admin';
import {
  VARS_BY_TEMPLATE_NAME,
  VARIABLE_DESCRIPTIONS,
  DEFAULT_CUSTOM_VARS,
} from '../../../lib/campaignTemplateVariables';

// Per-template visual metadata: icon, accent color, and a one-line purpose
// shown as the card subtitle so the admin understands at-a-glance what each
// template is for without having to expand it.
type TypeMeta = {
  icon: React.ReactNode;
  iconBg: string;     // tailwind bg class for the icon tile
  iconColor: string;  // tailwind text color for the icon
  buttonRing: string; // tailwind border + text classes for the outlined Edit button
  purpose: string;
};

const TYPE_META: Record<string, TypeMeta> = {
  tour_announcement: {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
        <path d="M3 11l18-5v12L3 13z" />
        <path d="M11.6 16.8a3 3 0 11-5.8-1.6" />
      </svg>
    ),
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-600',
    buttonRing: 'border-blue-200 text-blue-600 hover:bg-blue-50',
    purpose: 'Sent ~90 days before each tour stop',
  },
  reminder: {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
        <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 01-3.46 0" />
      </svg>
    ),
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-600',
    buttonRing: 'border-amber-200 text-amber-600 hover:bg-amber-50',
    purpose: 'Monthly nudge between announcement and final reminder',
  },
  final_reminder: {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
      </svg>
    ),
    iconBg: 'bg-red-50',
    iconColor: 'text-red-600',
    buttonRing: 'border-red-200 text-red-600 hover:bg-red-50',
    purpose: 'Urgent last call ~2 weeks before the tour',
  },
  activation: {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
        <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 11-7.778 7.778 5.5 5.5 0 017.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
      </svg>
    ),
    iconBg: 'bg-emerald-50',
    iconColor: 'text-emerald-600',
    buttonRing: 'border-emerald-200 text-emerald-600 hover:bg-emerald-50',
    purpose: 'Invites dormant studios to activate their account',
  },
  faculty_invitation_campaign: {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
        <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
        <path d="M6 12v5c3 3 9 3 12 0v-5" />
      </svg>
    ),
    iconBg: 'bg-purple-50',
    iconColor: 'text-purple-600',
    buttonRing: 'border-purple-200 text-purple-600 hover:bg-purple-50',
    purpose: 'Onboards new teaching faculty members',
  },
};

const DEFAULT_TYPE_META: TypeMeta = {
  icon: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <path d="M22 6l-10 7L2 6" />
    </svg>
  ),
  iconBg: 'bg-warm-50',
  iconColor: 'text-warm-500',
  buttonRing: 'border-warm-200 text-warm-600 hover:bg-warm-50',
  purpose: 'Custom campaign template',
};

interface TemplateFormValues {
  display_name: string;
  description: string;
  subject: string;
  preview_text: string;
  html_content: string;
  plain_text_content: string;
}

const EMPTY_FORM: TemplateFormValues = {
  display_name: '',
  description: '',
  subject: '',
  preview_text: '',
  html_content: '',
  plain_text_content: '',
};

export default function TemplatesTab() {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createOkLabel, setCreateOkLabel] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EmailTemplate | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteOkLabel, setDeleteOkLabel] = useState<string | null>(null);
  // SDA-195 follow-up: shows for the optional "Preview as show…" selector.
  // Loaded once here (not per-card) and passed down to each TemplateForm.
  const [shows, setShows] = useState<Show[]>([]);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const rows = await getEmailTemplates({ kind: 'campaign' });
      setTemplates(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    getShows().then(setShows).catch(() => {});
  }, []);

  const handleCreate = async (values: TemplateFormValues) => {
    try {
      setCreateError(null);
      const created = await createEmailTemplate({
        display_name: values.display_name,
        description: values.description || undefined,
        subject: values.subject,
        preview_text: values.preview_text || undefined,
        html_content: values.html_content,
        plain_text_content: values.plain_text_content || undefined,
      });
      setCreateOkLabel(created.display_name ?? created.name);
      setIsCreating(false);
      await load();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Create failed');
      throw err;
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const label = deleteTarget.display_name ?? deleteTarget.name;
    try {
      setDeleting(true);
      setDeleteError(null);
      await deleteEmailTemplate(deleteTarget.id);
      setDeleteOkLabel(label);
      setDeleteTarget(null);
      await load();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white rounded-xl border border-warm-200 p-6 animate-pulse">
            <div className="h-5 bg-warm-100 rounded w-1/3 mb-3" />
            <div className="h-4 bg-warm-100 rounded w-2/3" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
        {error}
      </div>
    );
  }

  const systemTemplates = templates.filter((t) => t.is_system_template);
  const customTemplates = templates.filter((t) => !t.is_system_template);

  return (
    <div className="space-y-3">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
        <p className="font-semibold mb-1">How template edits apply</p>
        <p>
          Edits here apply to <strong>new campaigns</strong> the system creates from now on
          (e.g. the next time the auto-queue runs). Campaigns already in the queue keep
          their currently-rendered email content — open a specific campaign's edit page
          to change its body directly.
        </p>
      </div>

      <div className="flex items-center justify-between gap-4 pt-1">
        <h2 className="text-sm font-semibold text-warm-700">Campaign Templates</h2>
        {!isCreating && (
          <button
            type="button"
            onClick={() => {
              setIsCreating(true);
              setCreateError(null);
              setCreateOkLabel(null);
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-xs font-semibold transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            New Template
          </button>
        )}
      </div>

      {createOkLabel && (
        <div className="px-3 py-2 bg-success-50 border border-success-200 rounded-lg text-xs text-success-700 flex items-center justify-between">
          <span>Template &quot;{createOkLabel}&quot; created.</span>
          <button type="button" onClick={() => setCreateOkLabel(null)} className="text-success-600 hover:text-success-800">×</button>
        </div>
      )}
      {deleteOkLabel && (
        <div className="px-3 py-2 bg-success-50 border border-success-200 rounded-lg text-xs text-success-700 flex items-center justify-between">
          <span>Template &quot;{deleteOkLabel}&quot; deleted.</span>
          <button type="button" onClick={() => setDeleteOkLabel(null)} className="text-success-600 hover:text-success-800">×</button>
        </div>
      )}
      {deleteError && (
        <div className="px-3 py-2 bg-danger-50 border border-danger-200 rounded-lg text-xs text-danger-700">{deleteError}</div>
      )}

      {isCreating && (
        <div className="bg-white rounded-xl border border-primary-200 p-4 sm:p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-warm-900">New Custom Template</h3>
            <button
              type="button"
              onClick={() => { setIsCreating(false); setCreateError(null); }}
              className="text-warm-400 hover:text-warm-700 text-xs"
            >
              Cancel
            </button>
          </div>
          {createError && (
            <div className="mb-3 px-3 py-2 bg-danger-50 border border-danger-200 rounded-lg text-xs text-danger-700">{createError}</div>
          )}
          <TemplateForm
            initialValues={EMPTY_FORM}
            templateName="__custom__"
            isSystemTemplate={false}
            saveLabel="Create Template"
            shows={shows}
            onSave={handleCreate}
            onCancel={() => { setIsCreating(false); setCreateError(null); }}
          />
        </div>
      )}

      {templates.length === 0 && !isCreating ? (
        <div className="p-6 bg-white rounded-xl border border-warm-200 text-warm-600">
          No campaign templates found.
        </div>
      ) : (
        <>
          {systemTemplates.length > 0 && (
            <>
              <h3 className="text-xs font-semibold text-warm-500 uppercase tracking-wide pt-2">System Templates</h3>
              {systemTemplates.map((tpl) => (
                <TemplateCard
                  key={tpl.id}
                  template={tpl}
                  shows={shows}
                  expanded={expandedId === tpl.id}
                  onToggle={() => setExpandedId(expandedId === tpl.id ? null : tpl.id)}
                  onSaved={load}
                  onRequestDelete={null}
                />
              ))}
            </>
          )}

          {customTemplates.length > 0 && (
            <>
              <h3 className="text-xs font-semibold text-warm-500 uppercase tracking-wide pt-4">Custom Templates</h3>
              {customTemplates.map((tpl) => (
                <TemplateCard
                  key={tpl.id}
                  template={tpl}
                  shows={shows}
                  expanded={expandedId === tpl.id}
                  onToggle={() => setExpandedId(expandedId === tpl.id ? null : tpl.id)}
                  onSaved={load}
                  onRequestDelete={() => setDeleteTarget(tpl)}
                />
              ))}
            </>
          )}
        </>
      )}

      {deleteTarget && (
        <ConfirmModal
          title={`Delete template "${deleteTarget.display_name ?? deleteTarget.name}"?`}
          message={
            "Existing campaigns that use this template keep their already-rendered copy — they're not affected. New campaigns won't be able to pick this template again. This cannot be undone."
          }
          confirmLabel={deleting ? 'Deleting…' : 'Delete Template'}
          confirmVariant="danger"
          onConfirm={handleConfirmDelete}
          onCancel={() => { if (!deleting) setDeleteTarget(null); }}
        />
      )}
    </div>
  );
}

interface TemplateCardProps {
  template: EmailTemplate;
  shows: Show[];
  expanded: boolean;
  onToggle: () => void;
  onSaved: () => void;
  onRequestDelete: (() => void) | null;
}

function TemplateCard({ template, shows, expanded, onToggle, onSaved, onRequestDelete }: TemplateCardProps) {
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveOk, setSaveOk] = useState(false);

  const headerLabel = template.display_name || template.name;
  const meta = TYPE_META[template.name] ?? DEFAULT_TYPE_META;

  const initialValues: TemplateFormValues = {
    display_name: template.display_name ?? '',
    description: template.description ?? '',
    subject: template.subject,
    preview_text: template.preview_text ?? '',
    html_content: template.html_content ?? template.content ?? '',
    plain_text_content: template.plain_text_content ?? '',
  };

  const handleSave = async (values: TemplateFormValues) => {
    try {
      setSaving(true);
      setSaveError(null);
      setSaveOk(false);
      await updateEmailTemplate(template.id, {
        display_name: values.display_name || null,
        subject: values.subject,
        preview_text: values.preview_text || null,
        html_content: values.html_content,
        plain_text_content: values.plain_text_content || null,
        description: values.description || null,
      });
      setSaveOk(true);
      onSaved();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
      throw err;
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`bg-white rounded-xl border overflow-hidden transition-shadow ${
        expanded ? 'border-warm-300 shadow-md' : 'border-warm-200 hover:shadow-sm'
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        className={`w-full flex items-center gap-4 px-4 sm:px-5 py-4 text-left group transition-colors ${
          expanded ? 'bg-warm-50/60' : 'hover:bg-warm-50/40'
        }`}
      >
        {/* Type icon */}
        <div className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${meta.iconBg} ${meta.iconColor}`}>
          {meta.icon}
        </div>

        {/* Title + purpose */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-warm-900 truncate text-base">{headerLabel}</h3>
            {template.is_system_template ? (
              <span className="shrink-0 text-[10px] uppercase tracking-wide font-semibold text-warm-500 bg-warm-100 px-1.5 py-0.5 rounded">System</span>
            ) : (
              <span className="shrink-0 text-[10px] uppercase tracking-wide font-semibold text-primary-700 bg-primary-50 px-1.5 py-0.5 rounded">Custom</span>
            )}
          </div>
          <p className="text-xs text-warm-500 mt-0.5 truncate">{template.is_system_template ? meta.purpose : (template.description || 'Custom campaign template')}</p>
        </div>

        {/* Last edited (hidden on small screens) */}
        {template.last_edited_at && (
          <div className="hidden md:block shrink-0 text-right">
            <p className="text-[11px] text-warm-400 leading-tight">Last edited</p>
            <p className="text-xs text-warm-500 leading-tight">
              {new Date(template.last_edited_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              {template.last_edited_by && <span className="text-warm-400"> by Admin</span>}
            </p>
          </div>
        )}

        {/* Delete button (custom templates only) */}
        {onRequestDelete && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onRequestDelete(); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); e.preventDefault(); onRequestDelete(); } }}
            aria-label="Delete template"
            className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg text-warm-400 hover:text-danger-600 hover:bg-danger-50 transition-colors cursor-pointer"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" /></svg>
          </span>
        )}

        {/* Outlined Edit button (type-colored) */}
        <span
          className={`hidden sm:inline-flex shrink-0 items-center px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors ${meta.buttonRing}`}
        >
          {expanded ? 'Editing' : 'Edit'}
        </span>

        {/* Chevron */}
        <svg
          className={`shrink-0 w-5 h-5 transition-transform ${expanded ? 'rotate-90 text-warm-600' : 'text-warm-400 group-hover:text-warm-600'}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {expanded && (
        <div className="border-t border-warm-100 p-4 sm:p-5 space-y-4">
          {saveError && (
            <div className="px-3 py-2 bg-danger-50 border border-danger-200 rounded-lg text-xs text-danger-700">⚠ {saveError}</div>
          )}
          {saveOk && (
            <div className="px-3 py-2 bg-success-50 border border-success-200 rounded-lg text-xs text-success-700">✓ Saved</div>
          )}
          <TemplateForm
            key={template.id}
            initialValues={initialValues}
            templateName={template.name}
            isSystemTemplate={template.is_system_template}
            saveLabel="Save Template"
            saving={saving}
            shows={shows}
            onSave={handleSave}
            onCancel={null}
            showSystemNameHint
          />
        </div>
      )}
    </div>
  );
}

// =============================================================================
// TemplateForm — reusable form fields used by both the edit panel (inside
// TemplateCard) and the inline create flow at the top of TemplatesTab.
// Single source of truth for the 6 inputs + variable picker so we can't drift
// between create and edit.
// =============================================================================

interface TemplateFormProps {
  initialValues: TemplateFormValues;
  templateName: string;
  isSystemTemplate: boolean;
  saveLabel: string;
  saving?: boolean;
  shows: Show[];
  onSave: (values: TemplateFormValues) => Promise<void>;
  onCancel: (() => void) | null;
  showSystemNameHint?: boolean;
}

function TemplateForm({
  initialValues,
  templateName,
  isSystemTemplate,
  saveLabel,
  saving: savingProp,
  shows,
  onSave,
  onCancel,
  showSystemNameHint,
}: TemplateFormProps) {
  const [displayName, setDisplayName] = useState(initialValues.display_name);
  const [description, setDescription] = useState(initialValues.description);
  const [subject, setSubject] = useState(initialValues.subject);
  const [previewText, setPreviewText] = useState(initialValues.preview_text);
  const [htmlContent, setHtmlContent] = useState(initialValues.html_content);
  const [plainText, setPlainText] = useState(initialValues.plain_text_content);
  const [showVarPicker, setShowVarPicker] = useState<null | 'subject' | 'previewText' | 'plainText'>(null);
  const [localSaving, setLocalSaving] = useState(false);

  // SDA-195: Preview + "send test to myself" operate on the CURRENT (possibly
  // unsaved) editor content, rendered server-side with sample values so the
  // preview is byte-identical to the test email.
  const [showPreview, setShowPreview] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [sendingTest, setSendingTest] = useState(false);
  const [testNotice, setTestNotice] = useState<{ ok: boolean; text: string } | null>(null);
  // SDA-195 follow-up: optional show to render the preview/test against real data.
  // '' = generic sample values (default, unchanged behavior).
  const [previewShowId, setPreviewShowId] = useState('');
  const activeShows = shows.filter((s) => s.is_active);
  const selectedShowName = previewShowId ? shows.find((s) => s.id === previewShowId)?.name : undefined;

  const allowedVars: readonly string[] = isSystemTemplate
    ? (VARS_BY_TEMPLATE_NAME[templateName] ?? [])
    : DEFAULT_CUSTOM_VARS;

  const buildPreviewContent = () => ({
    subject,
    html_content: htmlContent,
    preview_text: previewText || undefined,
    plain_text_content: plainText || undefined,
    show_id: previewShowId || undefined,
  });

  const handlePreview = async () => {
    setPreviewError(null);
    setShowPreview(true);
    setPreviewing(true);
    try {
      const res = await renderTemplatePreview(buildPreviewContent());
      setPreviewHtml(res.html);
      setPreviewSubject(res.subject);
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : 'Failed to render preview');
    } finally {
      setPreviewing(false);
    }
  };

  const handleSendTest = async () => {
    setTestNotice(null);
    setSendingTest(true);
    try {
      const res = await sendTestTemplateEmail(buildPreviewContent());
      setTestNotice({ ok: true, text: `Test sent to ${res.sent_to}. Check your inbox.` });
    } catch (err) {
      setTestNotice({ ok: false, text: err instanceof Error ? err.message : 'Failed to send test email' });
    } finally {
      setSendingTest(false);
    }
  };

  const handleInsertVar = (varName: string, target: 'subject' | 'previewText' | 'plainText') => {
    const tag = `{{${varName}}}`;
    if (target === 'subject') setSubject((s) => s + tag);
    else if (target === 'previewText') setPreviewText((s) => s + tag);
    else if (target === 'plainText') setPlainText((s) => s + tag);
    setShowVarPicker(null);
  };

  const submitting = savingProp ?? localSaving;

  const handleSubmit = async () => {
    try {
      setLocalSaving(true);
      await onSave({
        display_name: displayName,
        description: description,
        subject,
        preview_text: previewText,
        html_content: htmlContent,
        plain_text_content: plainText,
      });
    } catch {
      // parent surfaces errors
    } finally {
      setLocalSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Display Name */}
      <div>
        <label className="block text-xs font-semibold text-warm-700 mb-1.5">Display Name *</label>
        <input
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="e.g. Summer Promotion"
          className="w-full px-3 py-2 border border-warm-200 rounded-lg text-sm"
        />
        {showSystemNameHint && isSystemTemplate && (
          <p className="text-xs text-warm-400 mt-1">
            Shown as the card title in this list. The system identifier (<code className="font-mono text-[10px]">{templateName}</code>) is fixed.
          </p>
        )}
        {!isSystemTemplate && (
          <p className="text-xs text-warm-400 mt-1">
            Shown as the card title and in the campaign-create picker. The system identifier is auto-generated.
          </p>
        )}
      </div>

      {/* Description */}
      <div>
        <label className="block text-xs font-semibold text-warm-700 mb-1.5">Description <span className="text-warm-400 font-normal">(internal)</span></label>
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full px-3 py-2 border border-warm-200 rounded-lg text-sm"
        />
      </div>

      {/* Subject + variable picker */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-xs font-semibold text-warm-700">Subject *</label>
          <VarPickerButton
            allowedVars={allowedVars}
            onPick={(v) => handleInsertVar(v, 'subject')}
            open={showVarPicker === 'subject'}
            onToggle={() => setShowVarPicker(showVarPicker === 'subject' ? null : 'subject')}
          />
        </div>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full px-3 py-2 border border-warm-200 rounded-lg text-sm font-medium"
        />
      </div>

      {/* Preview text + variable picker */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-xs font-semibold text-warm-700">Preview Text (inbox preheader)</label>
          <VarPickerButton
            allowedVars={allowedVars}
            onPick={(v) => handleInsertVar(v, 'previewText')}
            open={showVarPicker === 'previewText'}
            onToggle={() => setShowVarPicker(showVarPicker === 'previewText' ? null : 'previewText')}
          />
        </div>
        <input
          type="text"
          value={previewText}
          onChange={(e) => setPreviewText(e.target.value)}
          maxLength={200}
          className="w-full px-3 py-2 border border-warm-200 rounded-lg text-sm"
          placeholder="Brief text shown in email clients before opening"
        />
        <p className="text-xs text-warm-400 mt-1">{previewText.length}/200 characters</p>
      </div>

      {/* HTML content (RichTextEditor) */}
      <div>
        <label className="block text-xs font-semibold text-warm-700 mb-1.5">Email Content (HTML) *</label>
        <RichTextEditor value={htmlContent} onChange={setHtmlContent} />
        <p className="text-xs text-warm-400 mt-1">
          Allowed variables for this template:{' '}
          {allowedVars.length === 0 ? (
            <span className="text-warm-500">none</span>
          ) : (
            allowedVars.map((v) => (
              <code key={v} className="bg-warm-100 px-1 mx-0.5 rounded">{`{{${v}}}`}</code>
            ))
          )}
        </p>
      </div>

      {/* Plain text fallback + variable picker */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-xs font-semibold text-warm-700">Plain Text Version (optional)</label>
          <VarPickerButton
            allowedVars={allowedVars}
            onPick={(v) => handleInsertVar(v, 'plainText')}
            open={showVarPicker === 'plainText'}
            onToggle={() => setShowVarPicker(showVarPicker === 'plainText' ? null : 'plainText')}
          />
        </div>
        <textarea
          value={plainText}
          onChange={(e) => setPlainText(e.target.value)}
          rows={4}
          className="w-full px-3 py-2 border border-warm-200 rounded-lg text-sm font-mono"
          placeholder="Plain-text fallback for clients that don't render HTML"
        />
      </div>

      {/* SDA-195: test-send result notice */}
      {testNotice && (
        <div
          className={`p-3 rounded-lg text-xs border ${
            testNotice.ok
              ? 'bg-success-50 border-success-200 text-success-800'
              : 'bg-danger-50 border-danger-200 text-danger-700'
          }`}
        >
          {testNotice.text}
        </div>
      )}

      {/* Save row */}
      <div className="flex flex-wrap items-center justify-between gap-2 pt-3 border-t border-warm-100">
        {/* SDA-195: Preview + Send test to myself — operate on current (unsaved) content */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Optional: render preview/test against a real show's live data */}
          <select
            value={previewShowId}
            onChange={(e) => setPreviewShowId(e.target.value)}
            title="Render the preview and test email with this show's real data (dates, pricing, city). Recipient is still only you."
            className="px-2 py-1.5 text-xs border border-warm-200 rounded-lg bg-white text-warm-700 max-w-[12rem]"
          >
            <option value="">Sample data (no show)</option>
            {activeShows.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={handlePreview}
            disabled={previewing}
            title="Preview this template with sample data"
            className="px-3 py-1.5 text-xs font-medium border border-primary-300 text-primary-600 rounded-lg hover:bg-primary-50 transition-colors disabled:opacity-50"
          >
            {previewing ? 'Loading…' : 'Preview'}
          </button>
          <button
            type="button"
            onClick={handleSendTest}
            disabled={sendingTest}
            title="Send a test copy of this template to your own inbox"
            className="px-3 py-1.5 text-xs font-medium border border-primary-300 text-primary-600 rounded-lg hover:bg-primary-50 transition-colors disabled:opacity-50"
          >
            {sendingTest ? 'Sending…' : 'Send test to myself'}
          </button>
        </div>
        <div className="flex items-center gap-2">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="px-4 py-2 text-warm-700 border border-warm-300 rounded-lg font-medium hover:bg-warm-50 disabled:opacity-50 text-sm"
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 text-sm"
          >
            {submitting ? 'Saving…' : saveLabel}
          </button>
        </div>
      </div>

      {/* SDA-195: Preview modal — mirrors the campaign editor's preview */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden border border-warm-200">
            <div className="flex items-center justify-between px-5 py-4 border-b border-warm-100">
              <div>
                <h3 className="text-sm font-semibold text-warm-900">Template Preview</h3>
                <p className="text-xs text-warm-400 mt-0.5">
                  {selectedShowName
                    ? `Previewing as: ${selectedShowName} — recipient-specific fields still use sample data`
                    : 'Sample data shown in place of real campaign data'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowPreview(false)}
                className="p-1.5 text-warm-400 hover:text-warm-600 hover:bg-warm-50 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {!previewing && !previewError && (
              <div className="bg-warm-50 border-b border-warm-100 px-5 py-3">
                <div className="flex gap-2 text-xs items-baseline">
                  <span className="text-warm-400 flex-shrink-0">Subject</span>
                  <span className="font-semibold text-warm-900 text-sm">{previewSubject || '(no subject)'}</span>
                </div>
              </div>
            )}
            {previewError ? (
              <div className="flex-1 p-6">
                <div className="px-3 py-2 bg-danger-50 border border-danger-200 rounded-lg text-xs text-danger-700">⚠ {previewError}</div>
              </div>
            ) : previewing ? (
              <div className="flex-1 flex items-center justify-center py-16">
                <div className="h-8 w-8 border-4 border-warm-200 border-t-primary-500 rounded-full animate-spin" />
              </div>
            ) : (
              <iframe
                srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"></head><body style="margin:0;padding:24px;">${previewHtml}</body></html>`}
                className="w-full border-0"
                style={{ height: 'calc(90vh - 200px)', maxHeight: '480px' }}
                title="Template Preview"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface VarPickerButtonProps {
  allowedVars: readonly string[];
  onPick: (varName: string) => void;
  open: boolean;
  onToggle: () => void;
}

function VarPickerButton({ allowedVars, onPick, open, onToggle }: VarPickerButtonProps) {
  return (
    <div className="relative">
      <button
        type="button"
        onClick={onToggle}
        className="text-xs text-primary-600 hover:underline font-medium"
      >
        # Insert Variable
      </button>
      {open && (
        <div className="absolute right-0 top-6 z-10 bg-white border border-warm-200 rounded-lg shadow-lg w-[calc(100vw-2rem)] sm:w-72 max-h-72 overflow-y-auto">
          {allowedVars.length === 0 ? (
            <div className="p-3 text-xs text-warm-500">No variables available for this template.</div>
          ) : (
            allowedVars.map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => onPick(v)}
                className="w-full text-left px-3 py-2 hover:bg-warm-50 border-b border-warm-100 last:border-b-0"
              >
                <code className="text-xs font-mono text-primary-700">{`{{${v}}}`}</code>
                <p className="text-xs text-warm-500 mt-0.5">{VARIABLE_DESCRIPTIONS[v] ?? ''}</p>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
